import React, { createContext, useContext, useCallback } from 'react';

/**
 * SavedColorsContext - Context for managing page-level saved colors
 *
 * This context provides saved colors data and actions to ColorField components
 * throughout the page builder hierarchy.
 *
 * Saved color data structure:
 * {
 *   id: string,      // Unique identifier (Date.now().toString(36))
 *   label: string,   // User-provided name
 *   value: object,   // Color value object
 *   createdAt: number // Date.now() timestamp
 * }
 */
const SavedColorsContext = createContext();

/**
 * SavedColorsProvider - Provider component for saved colors context
 *
 * @param {Object} props
 * @param {Array} props.savedColors - Array of saved color objects
 * @param {Function} props.onSaveColor - Callback to save a new color (value, label) => void
 * @param {Function} props.onDeleteSavedColor - Callback to delete a saved color (id) => void
 * @param {React.ReactNode} props.children - Child components
 */
export const SavedColorsProvider = ({
  savedColors = [],
  onSaveColor,
  onDeleteSavedColor,
  children,
}) => {
  // Wrap callbacks to handle null/undefined cases gracefully
  const handleSaveColor = useCallback((value, label) => {
    if (onSaveColor) {
      onSaveColor(value, label);
    }
  }, [onSaveColor]);

  const handleDeleteSavedColor = useCallback((id) => {
    if (onDeleteSavedColor) {
      onDeleteSavedColor(id);
    }
  }, [onDeleteSavedColor]);

  return (
    <SavedColorsContext.Provider
      value={{
        savedColors,
        onSaveColor: handleSaveColor,
        onDeleteSavedColor: handleDeleteSavedColor,
      }}
    >
      {children}
    </SavedColorsContext.Provider>
  );
};

/**
 * useSavedColors - Hook to access saved colors context
 *
 * @returns {Object} { savedColors, onSaveColor, onDeleteSavedColor }
 */
export const useSavedColors = () => {
  const context = useContext(SavedColorsContext);
  // Return empty defaults if not within provider (graceful degradation)
  if (!context) {
    return {
      savedColors: [],
      onSaveColor: () => {},
      onDeleteSavedColor: () => {},
    };
  }
  return context;
};

export default SavedColorsContext;
